<template>
  <p>
    <strong>Hinweis:</strong>
    Momentan kann pro Vorgang nur eine Person für das begleitete Fahren (BF17) erfasst
    werden. Wenn Sie mehrere Personen anmelden möchten, wiederholen Sie den Prozess
    einfach für jede Person.
  </p>

  <BasicTextfield
    :label="$t('requestAccompaniedDrivingForm.firstname.label')"
    :placeholder="$t('requestAccompaniedDrivingForm.firstname.placeholder')"
    :required="true"
    :status="firstNameTextfieldStatus"
    :value="firstName"
    @error="toggleFirstNameError()"
    @input="onFirstNameInputIsUpdated"
    @update:input="onFirstNameInput"
    formnovalidate="true"
    id="requestAccompaniedDrivingForm.firstname-input"
    max="100"
    type="text"
  >
    <template v-slot:info>{{
      $t('requestAccompaniedDrivingForm.firstname.info')
    }}</template>
    <template v-slot:error>{{ firstNameErrorMessage }}</template>
  </BasicTextfield>

  <BasicTextfield
    :label="$t('requestAccompaniedDrivingForm.lastname.label')"
    :placeholder="$t('requestAccompaniedDrivingForm.lastname.placeholder')"
    :required="true"
    :status="lastNameTextfieldStatus"
    :value="lastName"
    @error="toggleLastNameError()"
    @input="onLastNameInputIsUpdated"
    @update:input="onLastNameInput"
    formnovalidate="true"
    id="requestAccompaniedDrivingForm.lastname-input"
    max="100"
    type="text"
  >
    <template v-slot:info>{{
      $t('requestAccompaniedDrivingForm.lastname.info')
    }}</template>
    <template v-slot:error>{{ lastNameErrorMessage }}</template>
  </BasicTextfield>

  <DateInput
    :label="$t('requestAccompaniedDrivingForm.dateOfBirth.label')"
    :minValidYear="16"
    :maxValidYear="18"
    :placeholder="$t('requestAccompaniedDrivingForm.dateOfBirth.placeholder')"
    :required="true"
    :status="dateOfBirthTextfieldStatus"
    @update:input="notifyInputDate"
    @input="notifyInputDate"
    @update:invalid-date="notifyErrorDate"
    autocomplete="off"
    id="creation.birthdate"
    mask="DD.MM.YYYY"
    v-model="dateOfBirth"
  >
    <template v-slot:error>
      <div
        v-if="!ageBetween16and18"
        v-dompurify-html="$t('requestAccompaniedDrivingForm.dateOfBirth.errors.between')"
      ></div>
      <div
        v-else
        v-dompurify-html="$t('requestAccompaniedDrivingForm.dateOfBirth.errors.invalid')"
      ></div>
    </template>
    <template v-slot:info>
      <div v-dompurify-html="$t('requestAccompaniedDrivingForm.dateOfBirth.info')"></div>
    </template>
  </DateInput>
</template>

<script lang="ts" setup>
import { computed, type PropType, ref, watch } from 'vue'
import {
  BasicTextfield,
  DateInput,
  InputStatus,
  type PolicyWithIconAndTitles
} from 'de-zurich-ui-library'
// Account holder options now represented as array of { label, value }
import { useI18n } from 'vue-i18n'
import {
  checkIfBirthdayInputIsValid,
  isAgeBetween
} from '@/components/account-management/accountcreation/accountcreationUtils'
import { requestAccompaniedDrivingCertificate } from '@/stores/motorService'
import { navigate } from '@/utils/NavigationUtils'
import { usePoliciesStore } from '@/stores/policiesStore'

const { t } = useI18n()
const policiesStore = usePoliciesStore()

const props = defineProps({
  policies: {
    type: Object as PropType<PolicyWithIconAndTitles[]>,
    required: true
  },
  formStartDate: {
    type: String,
    required: true
  }
})

const emit = defineEmits<{
  formLoading: [value: boolean]
  formSubmitDisabled: [value: boolean]
  showErrorTile: [value: boolean]
  formResetButton: [none: void]
}>()

const nameWhitelistRegex = /^\p{L}[\p{L}\s'-]{0,99}$/u

// Firstname
const firstName = ref('')
const firstNameTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const firstNameErrorMessage = ref('')

const onFirstNameInputIsUpdated = (val: string): void => {
  firstName.value = val
  validateInputs()
}

const onFirstNameInput = (): void => {
  firstNameTextfieldStatus.value = InputStatus.DEFAULT
}

const toggleFirstNameError = (): void => {
  firstNameTextfieldStatus.value = InputStatus.ERROR
}

// Lastname
const lastName = ref('')
const lastNameTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const lastNameErrorMessage = ref('')

const onLastNameInputIsUpdated = (val: string): void => {
  lastName.value = val
  validateInputs()
}

const onLastNameInput = (): void => {
  lastNameTextfieldStatus.value = InputStatus.DEFAULT
}

const toggleLastNameError = (): void => {
  lastNameTextfieldStatus.value = InputStatus.ERROR
}

// DateOfBirth
const dateOfBirth = ref('')
const dateOfBirthTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)

const notifyErrorDate = (): void => {
  dateOfBirthTextfieldStatus.value = InputStatus.ERROR
}

const notifyInputDate = (): void => {
  dateOfBirthTextfieldStatus.value = ageBetween16and18.value
    ? checkIfBirthdayInputIsValid(dateOfBirth.value)
    : InputStatus.ERROR
}

const ageBetween16and18 = computed(() => isAgeBetween(dateOfBirth.value, 16, 18))

// Common Functions

const formSubmit = async (): Promise<void> => {
  validateInputs()

  // submit form to backend
  try {
    // Aufruf des Backends mit URL /certificates/accompanied-driving
    // und Daten: Liste mit VSNR, Kennzeichen, Vorname, Nachname, Geburtsdatum Jugendlicher
    requestAccompaniedDrivingCertificate(
      props.policies[0]!.policyId,
      firstName.value,
      lastName.value,
      dateOfBirth.value
    ).then((response: Response) => {
      if (response.ok) {
        const licensePlates = props.policies[0]!.secondTitle
        policiesStore.setAccompaniedDrivingThankYouValues({
          licensePlate: licensePlates
        })
        navigate(`/danke?bescheinigung-fuer-begleitetes-fahren-anfordern`)
      } else {
        emit('showErrorTile', true)
      }
    })
  } catch {
    emit('showErrorTile', true)
  }
}

function validateInputs() {
  validateFirstName()
  validateLastName()
  if (isAgeBetween(dateOfBirth.value, 16, 18))
    dateOfBirthTextfieldStatus.value = checkIfBirthdayInputIsValid(dateOfBirth.value)
}

watch(
  [firstNameTextfieldStatus, lastNameTextfieldStatus, dateOfBirthTextfieldStatus],
  () => {
    validateInputs()

    const isFormInvalid =
      firstNameTextfieldStatus.value !== InputStatus.VALID ||
      lastNameTextfieldStatus.value !== InputStatus.VALID ||
      dateOfBirthTextfieldStatus.value !== InputStatus.VALID

    emit('formSubmitDisabled', isFormInvalid)
    return isFormInvalid
  }
)

// Helper Functions
const validateFirstName = (): void => {
  if (firstName.value.trim().length <= 1) {
    firstNameTextfieldStatus.value = InputStatus.DEFAULT
  } else if (
    firstName.value &&
    firstName.value.trim() !== '' &&
    nameWhitelistRegex.test(firstName.value)
  ) {
    firstNameTextfieldStatus.value = InputStatus.VALID
    firstNameErrorMessage.value = ''
  } else {
    firstNameTextfieldStatus.value = InputStatus.ERROR
    firstNameErrorMessage.value = t(
      'requestAccompaniedDrivingForm.firstname.errors.invalid'
    )
  }
}

const validateLastName = (): void => {
  if (lastName.value.trim().length <= 1) {
    lastNameTextfieldStatus.value = InputStatus.DEFAULT
  } else if (
    lastName.value &&
    lastName.value.trim() !== '' &&
    nameWhitelistRegex.test(lastName.value)
  ) {
    lastNameTextfieldStatus.value = InputStatus.VALID
    lastNameErrorMessage.value = ''
  } else {
    lastNameTextfieldStatus.value = InputStatus.ERROR
    lastNameErrorMessage.value = t(
      'requestAccompaniedDrivingForm.lastname.errors.invalid'
    )
  }
}

defineExpose({ formSubmit })
</script>
